import { warn } from '@execonline-inc/logging';
import { noop } from '@kofno/piper';
import * as ActionCable from '@rails/actioncable';
import { Cable } from 'actioncable';
import { makeAutoObservable, observable } from 'mobx';
import { Task } from 'taskarian';
import { v4 as uuid } from 'uuid';
import { readVarT } from '../../../../Assertions/environments';
import {
  CableInitializationError,
  State,
  WebsocketInitializationError,
  errorMessage,
  initialize,
  loading,
  ready,
} from './Types';

const createConsumerT = (url: string): Task<CableInitializationError, Cable> => {
  return new Task<CableInitializationError, Cable>((reject, resolve) => {
    try {
      resolve(ActionCable.createConsumer(url));
    } catch (error) {
      warn('Unable to create consumer', error);
      reject({ kind: 'cable-initialization-error', key: 'Unable to create consumer' });
    }
    return noop;
  });
};

export class CableStore {
  @observable state: State = initialize();

  constructor() {
    makeAutoObservable(this);
  }

  private connected = (cable: Cable) => {
    this.state = ready(cable, uuid());
  };

  private connectionFailed = (error: WebsocketInitializationError) => {
    const message = errorMessage(error);

    warn(message);
    this.state = {
      kind: 'error',
      message,
    };
  };

  initialize = () => {
    this.state = loading();
    Task.succeed<WebsocketInitializationError, {}>({})
      .andThen(() => readVarT('VITE_ENTERPRISE_WEBSOCKETS_URL'))
      .andThen(createConsumerT)
      .fork(this.connectionFailed, this.connected);
  };

  disconnect = () => {
    if (this.state.kind === 'ready') {
      this.state.cable.disconnect();
      this.state = initialize();
    }
  };

  reconnect = () => {
    if (this.state.kind === 'initialize') {
      this.state = loading();
      Task.succeed<WebsocketInitializationError, {}>({})
        .andThen(() => readVarT('VITE_ENTERPRISE_WEBSOCKETS_URL'))
        .andThen(createConsumerT)
        .fork(this.connectionFailed, (cable) => {
          this.state = ready(cable, uuid());
        });
    }
  };
}

const cableStore = new CableStore();
cableStore.initialize();
export { cableStore };
